import { useRef } from 'react'
import cx from 'classnames'
import { useVirtualizer } from '@tanstack/react-virtual'
import ImportedGoodListRow from './ImportedGoodListRow'
import type { ImportedGoodExtended } from '@/features/cbam-reporting/hooks/useImportedGoodsFilters'
import { Partnership } from '@/features/suppliers/hooks/partnerships'
import { Installation } from '@/features/suppliers/hooks/installations'
import { Importer } from '@/features/importers/hooks/importers'
import { GoodsFamily } from '@/common'
import { BatchLiabilityResultItem } from '@/features/cbam-reporting/hooks/cbamLiability'

/** Approximate table row height (ListRow py-3 + grid content). */
const TABLE_ROW_ESTIMATE_PX = 88
/** Initial card row estimate before measureElement runs. */
const CARD_ROW_ESTIMATE_PX = 320

export type ImportedGoodsVirtualRowsProps = {
  layout: 'table' | 'card'
  filteredGoods: ImportedGoodExtended[]
  gridTemplateColumns: string
  partnershipById: Map<string, Partnership>
  importerById: Map<string, Importer>
  installationById: Map<string, Installation>
  getLiabilityDataForGood: (
    good: ImportedGoodExtended,
    index: number
  ) => BatchLiabilityResultItem | null
  isRepresentative: boolean
  isCompleted: boolean
  isSelected: (id: string) => boolean
  toggleSelect: (id: string) => void
  onEdit: (
    importedGood: ImportedGoodExtended,
    supplierName: string,
    installationName: string,
    goodsFamily?: GoodsFamily
  ) => void
  onView: (importedGood: ImportedGoodExtended) => void
  onDelete: (importedGood: ImportedGoodExtended) => void
}

export function ImportedGoodsVirtualRows({
  layout,
  filteredGoods,
  gridTemplateColumns,
  partnershipById,
  importerById,
  installationById,
  getLiabilityDataForGood,
  isRepresentative,
  isCompleted,
  isSelected,
  toggleSelect,
  onEdit,
  onView,
  onDelete
}: ImportedGoodsVirtualRowsProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const isTable = layout === 'table'

  const virtualizer = useVirtualizer({
    count: filteredGoods.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => (isTable ? TABLE_ROW_ESTIMATE_PX : CARD_ROW_ESTIMATE_PX),
    overscan: 8,
    gap: isTable ? 0 : 16
  })

  return (
    <div
      ref={scrollRef}
      className={cx(
        'w-full max-h-[550px] overflow-y-auto overflow-x-hidden',
        isTable ? 'bg-background-surface' : 'p-4 bg-background-surface'
      )}
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative'
        }}
      >
        {virtualizer.getVirtualItems().map(virtualRow => {
          const good = filteredGoods[virtualRow.index]
          return (
            <div
              key={good.id}
              data-index={virtualRow.index}
              ref={isTable ? undefined : virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`
              }}
            >
              <ImportedGoodListRow
                layout={layout}
                gridTemplateColumns={gridTemplateColumns}
                importedGood={good}
                supplier={
                  good.partnershipId ? partnershipById.get(good.partnershipId) ?? null : null
                }
                importer={good.importerId ? importerById.get(good.importerId) ?? null : null}
                installation={
                  good.installationId ? installationById.get(good.installationId) ?? null : null
                }
                liabilityData={getLiabilityDataForGood(good, virtualRow.index)}
                isRepresentative={isRepresentative}
                onEdit={onEdit}
                onView={onView}
                isEditable={!isCompleted}
                onDelete={onDelete}
                showCheckbox={!isCompleted}
                isSelected={isSelected(good.id)}
                onToggleSelect={toggleSelect}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
